<template>
  <div>
    <b-container fluid>
      <b-row>
        <b-col cols="12">
          <b-card>
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">{{ $t('navigation.title') }}</h5>
                <div class="text-muted small">
                  <div>
                    {{ currentVesselInfo.name }} |
                    Lat: {{ simulatedPosition.lat ? simulatedPosition.lat.toFixed(6) : ecdisData.coordinates.lat }}° |
                    Lon: {{ simulatedPosition.lon ? simulatedPosition.lon.toFixed(6) : ecdisData.coordinates.lon }}° |
                    Scale: {{ ecdisData.scale }}
                  </div>
                  <div v-if="lastRefreshTime" class="text-center mt-1">
                    Last update: {{ lastRefreshTime }}
                  </div>
                </div>
              </div>
            </template>
            <b-row>
              <!-- Панель управления слева -->
              <b-col cols="12" lg="3">
                <div class="control-panel p-2 mb-3">

                  <!-- Управление картой -->
                  <div class="control-section mb-3">
                    <h6 class="mb-2">{{ $t('navigation.mapControl') }}</h6>

                    <!-- Масштаб (Zoom) -->
                    <div class="control-item mb-2">
                      <label class="form-label mb-2">{{ $t('navigation.zoom') }}</label>
                      <div class="d-flex gap-2">
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="handleZoom('in')"
                          class="flex-fill"
                        >
                          <b-icon icon="zoom-in"></b-icon> {{ $t('navigation.zoomIn') }}
                        </b-button>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="handleZoom('out')"
                          class="flex-fill"
                        >
                          <b-icon icon="zoom-out"></b-icon> {{ $t('navigation.zoomOut') }}
                        </b-button>
                      </div>
                    </div>

                    <!-- Наклон (Pitch) -->
                    <div class="control-item mb-2">
                      <label class="form-label mb-2">Tilt</label>
                      <div class="d-flex gap-2">
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="handleTilt('in')"
                          class="flex-fill"
                        >
                          ↗ Pitch In
                        </b-button>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="handleTilt('out')"
                          class="flex-fill"
                        >
                          ↙ Pitch Out
                        </b-button>
                      </div>
                    </div>

                    <!-- Поворот (Yaw) -->
                    <div class="control-item mb-2">
                      <label class="form-label mb-2">Rotation</label>
                      <div class="d-flex gap-2">
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="handleRotation('counterclockwise')"
                          class="flex-fill"
                        >
                          ↺ CCW
                        </b-button>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="handleRotation('clockwise')"
                          class="flex-fill"
                        >
                          ↻ CW
                        </b-button>
                      </div>
                    </div>

                    <!-- 3D View переключатель -->
                    <div class="control-item mb-2 d-flex align-items-center">
                      <label class="form-label mb-0 me-2" style="min-width: 100px; font-size: 0.875rem;">3D View:</label>
                      <b-form-checkbox
                        v-model="displaySettings.is3DEnabled"
                        class="mb-0"
                      >
                      </b-form-checkbox>
                    </div>
                  </div>

                  <hr>

                  <!-- Кнопки навигации -->
                  <div class="control-section mb-3">
                    <h6 class="mb-2">Movement</h6>
                    <div class="navigation-buttons text-center">
                      <div class="d-flex justify-content-center mb-2">
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="moveMap('up')"
                        >
                          ↑
                        </b-button>
                      </div>
                      <div class="d-flex justify-content-center align-items-center mb-2">
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="moveMap('left')"
                          class="me-2"
                        >
                          ←
                        </b-button>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="centerMap()"
                          class="mx-2"
                        >
                          <b-icon icon="geo-alt-fill"></b-icon>
                        </b-button>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="moveMap('right')"
                          class="ms-2"
                        >
                          →
                        </b-button>
                      </div>
                      <div class="d-flex justify-content-center">
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="moveMap('down')"
                        >
                          ↓
                        </b-button>
                      </div>
                    </div>
                  </div>

                  <hr>

                  <!-- Кнопки управления -->
                  <div class="mt-3">
                    <!-- <b-button
                      variant="warning"
                      size="sm"
                      @click="resetSettings"
                      block
                      class="mb-2"
                    >
                      Reset All Settings
                    </b-button> -->

                    <b-button
                      variant="info"
                      size="sm"
                      @click="refreshImage(true)"
                      block
                      :disabled="imageLoading"
                    >
                      <b-spinner v-if="imageLoading" small class="me-1"></b-spinner>
                      Refresh Image
                    </b-button>
                  </div>
                </div>
              </b-col>

              <!-- Основная область с ECDIS изображением -->
              <b-col cols="12" lg="9">
                <div class="ecdis-container">
                  <div class="ecdis-image-wrapper">
                    <!-- Загружаемое изображение ECDIS -->
                    <img
                      v-if="!imageError && ecdisData.imageUrl"
                      :src="ecdisData.imageUrl"
                      alt="ECDIS Display"
                      class="ecdis-image"
                      @load="onImageLoad"
                      @error="onImageError"
                    >

                    <!-- Placeholder если нет изображения -->
                    <div v-if="imageError || !ecdisData.imageUrl" class="placeholder-display">
                      <div class="placeholder-content">
                        <div style="font-size: 3rem; margin-bottom: 1rem;">🚢</div>
                        <h4>ECDIS Display</h4>
                        <p v-if="imageError">Failed to load ECDIS image</p>
                        <p v-else>Loading ECDIS data...</p>
                        <p class="text-muted">Navigation controls are active</p>
                      </div>
                    </div>

                    <!-- Статус загрузки -->
                    <div v-if="imageLoading" class="loading-overlay">
                      <b-spinner variant="primary"></b-spinner>
                      <p class="mt-2">Loading ECDIS data...</p>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  getECDISData,
  refreshECDISImage,
  updateShipPosition,
  moveECDISMap,
  rotateECDISMap,
  tiltECDISMap,
  zoomECDISMap,
  resetECDISSettings
} from '@/helpers/apiNavigation';

import {mapStores} from "pinia";

import {useFleetStore} from "@/stores/fleet";

import {
  loadFleetAISData
} from "@/helpers/apiFleet";

export default {
  name: 'Navigation',
  data() {
    return {
      // Тестовый device ID (не нужен для новых команд)
      selectedDevice: 'B850-1177-BDF6-58DA',

      // Настройки отображения
      displaySettings: {
        is3DEnabled: false
      },

      // Данные ECDIS с сервера
      ecdisData: {
        imageUrl: '',
        scale: '',
        coordinates: {
          lat: 0,
          lon: 0
        }
      },

      // Состояние загрузки изображения
      imageLoading: false,
      imageError: false,

      // Интервал для автоматического обновления картинки
      imageRefreshInterval: null,

      // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
      // Интервал для симуляции движения корабля
      shipMovementInterval: null,

      // Текущая симулированная позиция корабля
      simulatedPosition: {
        lat: 0,
        lon: 0,
        sog: 0, // Speed Over Ground в узлах
        cog: 0  // Course Over Ground в градусах
      },
      // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======

      // Время последнего обновления
      lastRefreshTime: null
    }
  },

  async mounted() {
    // Инициализация компонента
    console.log('Navigation component mounting...');

    const {fleetStore} = this;

    try {
      // Загружаем AIS данные
      await loadFleetAISData();
      console.log('AIS data loaded:', fleetStore.AISData);

      // Инициализируем навигацию с реальными данными
      await this.initializeNavigation();

      // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
      // Запускаем симуляцию движения корабля
      this.startShipMovementSimulation();
      // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======

      // Запускаем автоматическое обновление картинки каждые 1.5 секунд
      this.startImageRefresh();
    } catch (err) {
      console.error(`Error loading AIS data:`, err);
      // Если не удалось загрузить AIS данные, используем хардкоженные
      await this.initializeNavigation();

      // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
      this.startShipMovementSimulation();
      // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======

      this.startImageRefresh();
    }
  },

  beforeDestroy() {
    // Очищаем интервал при уничтожении компонента
    this.stopImageRefresh();

    // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
    this.stopShipMovementSimulation();
    // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======
  },
  computed: {
    ...mapStores(useFleetStore),

    // Информация о текущем судне
    currentVesselInfo() {
      const targetDeviceId = 'A86F-78B7-5567-06C8';

      if (this.fleetStore.AISData && this.fleetStore.AISData[targetDeviceId]) {
        const aisData = this.fleetStore.AISData[targetDeviceId];
        return {
          name: aisData.payload?.VesselName || 'Unknown Vessel',
          mmsi: aisData.payload?.Mmsi || 'N/A',
          callSign: aisData.payload?.CallSign || 'N/A',
          destination: aisData.destination || 'N/A',
          speed: aisData.speedKnots || 0,
          course: aisData.payload?.CourseOverGround || 0
        };
      }

      return {
        name: 'Test Vessel',
        mmsi: 'N/A',
        callSign: 'N/A',
        destination: 'N/A',
        speed: 0,
        course: 0
      };
    }
  },

  methods: {
    // Инициализация навигации
    async initializeNavigation() {
      console.log('Navigation component initialized');

      // Получаем начальные координаты из AIS данных
      const initialPosition = this.getInitialPosition();
      console.log('Using initial position:', initialPosition);

      // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
      // Инициализируем симулированную позицию
      this.simulatedPosition = {
        lat: initialPosition.lat,
        lon: initialPosition.lon,
        sog: initialPosition.sog,
        cog: initialPosition.cog
      };
      // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======

      // Автоматически загружаем данные ECDIS
      await this.loadECDISData(initialPosition);
    },

    // Получение начальной позиции из AIS данных
    getInitialPosition() {
      const targetDeviceId = 'A86F-78B7-5567-06C8';

      // Проверяем, есть ли AIS данные
      if (this.fleetStore.AISData && this.fleetStore.AISData[targetDeviceId]) {
        const aisData = this.fleetStore.AISData[targetDeviceId];

        return {
          lat: aisData.lat,
          lon: aisData.lon,
          depth: 10, // Дефолтное значение глубины
          sog: aisData.speedKnots || 0,
          cog: aisData.payload?.CourseOverGround || 0
        };
      }

      // Если AIS данных нет, используем хардкоженные значения
      console.warn('AIS data not available, using hardcoded values');
      return {
        lat: 59.98,
        lon: 29.70,
        depth: 1,
        sog: 20,
        cog: 80
      };
    },

    // Загрузка данных ECDIS
    async loadECDISData(initialPosition = null) {
      this.imageLoading = true;
      this.imageError = false;

      try {
        console.log('Loading ECDIS data with position:', initialPosition);
        const data = await getECDISData(initialPosition);
        this.ecdisData = data;
        this.lastRefreshTime = new Date().toLocaleTimeString();
        console.log('ECDIS data loaded:', data);
      } catch (error) {
        console.error('Failed to load ECDIS data:', error);
        this.imageError = true;
      } finally {
        this.imageLoading = false;
      }
    },

    // Запуск автоматического обновления картинки
    startImageRefresh() {
      // Обновляем картинку каждые 1.5 секунд
      this.imageRefreshInterval = setInterval(async () => {
        // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
        // Используем updateShipPosition для обновления позиции без мешающего воздействия на управление картой
        await this.refreshImageWithPosition();
        // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======
      }, 1500);

      console.log('Image auto-refresh started (every 1.5 seconds) using updateship');
    },

    // Остановка автоматического обновления картинки
    stopImageRefresh() {
      if (this.imageRefreshInterval) {
        clearInterval(this.imageRefreshInterval);
        this.imageRefreshInterval = null;
        console.log('Image auto-refresh stopped');
      }
    },

    // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
    // Запуск симуляции движения корабля
    startShipMovementSimulation() {
      // Обновляем позицию корабля каждую секунду
      this.shipMovementInterval = setInterval(() => {
        this.updateShipPosition();
      }, 1000);

      console.log('Ship movement simulation started (every 1 second)');
    },

    // Остановка симуляции движения корабля
    stopShipMovementSimulation() {
      if (this.shipMovementInterval) {
        clearInterval(this.shipMovementInterval);
        this.shipMovementInterval = null;
        console.log('Ship movement simulation stopped');
      }
    },

    // Обновление позиции корабля на основе курса и скорости
    updateShipPosition() {
      if (this.simulatedPosition.sog <= 0) {
        return; // Корабль не движется
      }

      // Конвертируем скорость из узлов в градусы в секунду
      // 1 узел = 1 морская миля в час = 1852 метра в час
      // 1 градус широты ≈ 111320 метров
      // 1 градус долготы ≈ 111320 * cos(latitude) метров

      const speedKnots = this.simulatedPosition.sog;
      const courseRadians = (this.simulatedPosition.cog * Math.PI) / 180;

      // Скорость в метрах в секунду
      const speedMs = speedKnots * 1852 / 3600;

      // Расстояние за 1 секунду в метрах
      const distanceMeters = speedMs;

      // Изменение широты и долготы
      const deltaLat = (Math.cos(courseRadians) * distanceMeters) / 111320;
      const deltaLon = (Math.sin(courseRadians) * distanceMeters) / (111320 * Math.cos(this.simulatedPosition.lat * Math.PI / 180));

      // Обновляем позицию
      this.simulatedPosition.lat += deltaLat;
      this.simulatedPosition.lon += deltaLon;

      console.log(`Ship moved to: ${this.simulatedPosition.lat.toFixed(6)}, ${this.simulatedPosition.lon.toFixed(6)} | Speed: ${speedKnots} knots | Course: ${this.simulatedPosition.cog}°`);
    },

    // Обновление картинки с текущей симулированной позицией
    async refreshImageWithPosition() {
      try {
        // Используем новую функцию updateship для обновления только позиции корабля
        const newPosition = {
          lat: this.simulatedPosition.lat,
          lon: this.simulatedPosition.lon,
          sog: this.simulatedPosition.sog,
          cog: this.simulatedPosition.cog
        };

        // Обновляем позицию корабля без переинициализации карты
        const success = await updateShipPosition(newPosition);

        if (success) {
          // Даем серверу время обработать новую позицию
          await new Promise(resolve => setTimeout(resolve, 100));

          // Затем получаем обновленную картинку с уникальным timestamp
          const imageData = await refreshECDISImage();
          if (imageData.imageUrl) {
            // Добавляем уникальный параметр для избежания кеширования
            this.ecdisData.imageUrl = `${imageData.imageUrl}&pos=${this.simulatedPosition.lat.toFixed(6)}_${this.simulatedPosition.lon.toFixed(6)}`;
            this.ecdisData.coordinates.lat = this.simulatedPosition.lat;
            this.ecdisData.coordinates.lon = this.simulatedPosition.lon;
            this.lastRefreshTime = new Date().toLocaleTimeString();
            console.log(`ECDIS image updated with ship position: ${this.simulatedPosition.lat.toFixed(6)}, ${this.simulatedPosition.lon.toFixed(6)}`);
          }
        }
      } catch (error) {
        console.error('Failed to refresh ECDIS image with new position:', error);
      }
    },
    // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======

    // Обновление картинки
    async refreshImage() {
      try {
        // Показываем индикатор загрузки только для ручного обновления
        const isManualRefresh = arguments.length > 0 && arguments[0] === true;
        if (isManualRefresh) {
          this.imageLoading = true;
        }

        const imageData = await refreshECDISImage();
        if (imageData.imageUrl) {
          this.ecdisData.imageUrl = imageData.imageUrl;
          this.lastRefreshTime = new Date().toLocaleTimeString();
          console.log('ECDIS image refreshed at:', this.lastRefreshTime);
        }
      } catch (error) {
        console.error('Failed to refresh ECDIS image:', error);
        // Не устанавливаем imageError для автоматического обновления
      } finally {
        this.imageLoading = false;
      }
    },

    // Обработка команд масштабирования
    async handleZoom(direction) {
      try {
        console.log('Zoom command:', direction);
        const success = await zoomECDISMap(direction);
        if (success) {
          // Обновляем картинку после успешной команды
          await this.refreshImage();
        }
      } catch (error) {
        console.error('Failed to execute zoom command:', error);
      }
    },

    // Обработка команд наклона
    async handleTilt(direction) {
      try {
        console.log('Tilt command:', direction);
        const success = await tiltECDISMap(direction);
        if (success) {
          // Обновляем картинку после успешной команды
          await this.refreshImage();
        }
      } catch (error) {
        console.error('Failed to execute tilt command:', error);
      }
    },

    // Обработка команд поворота
    async handleRotation(direction) {
      try {
        console.log('Rotation command:', direction);
        const success = await rotateECDISMap(direction);
        if (success) {
          // Обновляем картинку после успешной команды
          await this.refreshImage();
        }
      } catch (error) {
        console.error('Failed to execute rotation command:', error);
      }
    },

    // Обновление настроек ECDIS (теперь только для 3D режима)
    async updateECDISSettings() {
      try {
        console.log('3D View setting changed:', this.displaySettings.is3DEnabled);
        // TODO: Возможно понадобится отдельная команда для 3D режима
        // Пока просто логируем изменение
      } catch (error) {
        console.error('Failed to update display settings:', error);
      }
    },

    // Перемещение карты
    async moveMap(direction) {
      try {
        console.log('Moving map:', direction);
        const success = await moveECDISMap(direction);
        if (success) {
          // Обновляем картинку после успешной команды
          await this.refreshImage();
        }
      } catch (error) {
        console.error('Failed to move map:', error);
      }
    },

    // Центрирование карты на корабле
    async centerMap() {
      // ====== ВРЕМЕННАЯ СИМУЛЯЦИЯ ДВИЖЕНИЯ КОРАБЛЯ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
      // Используем симулированную позицию для центрирования
      const currentPosition = {
        lat: this.simulatedPosition.lat,
        lon: this.simulatedPosition.lon,
        depth: 10,
        sog: this.simulatedPosition.sog,
        cog: this.simulatedPosition.cog
      };
      // ====== КОНЕЦ ВРЕМЕННОЙ СИМУЛЯЦИИ ======

      console.log('Centering map with current position:', currentPosition);
      await this.loadECDISData(currentPosition);
    },

    // Сброс всех настроек к дефолтным значениям
    async resetSettings() {
      try {
        console.log('Resetting ECDIS settings');
        const data = await resetECDISSettings(this.selectedDevice);

        if (data) {
          // Сбрасываем локальные настройки
          this.displaySettings.is3DEnabled = false;

          // Обновляем данные ECDIS
          this.ecdisData = {
            imageUrl: data.imageUrl,
            scale: data.scale,
            coordinates: data.coordinates
          };

          // Обновляем картинку после сброса
          await this.refreshImage();

          console.log('Settings reset successfully');
        }
      } catch (error) {
        console.error('Failed to reset settings:', error);
      }
    },

    // Обработка загрузки изображения
    onImageLoad() {
      this.imageLoading = false;
      this.imageError = false;
      console.log('ECDIS image loaded successfully');
    },

    // Обработка ошибки загрузки изображения
    onImageError() {
      this.imageLoading = false;
      this.imageError = true;
      console.log('Failed to load ECDIS image');
    }
  }
}
</script>

<style scoped>
.control-panel {
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.control-section {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.control-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.control-item {
  background: white;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.control-item .d-flex.gap-2 {
  gap: 0.5rem !important;
}

.control-item .btn.flex-fill {
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  padding: 0.375rem 0.5rem;
}

.form-label {
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
  color: #495057;
}

.form-range {
  margin-top: 5px;
  width: 100%;
}

.form-select {
  width: 100%;
  padding: 0.375rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.navigation-buttons .btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2px;
}

.ecdis-container {
  position: relative;
  width: 100%;
  height: calc(100vh - 250px);
  min-height: 500px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(45deg, #001122 25%, transparent 25%),
              linear-gradient(-45deg, #001122 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #001122 75%),
              linear-gradient(-45deg, transparent 75%, #001122 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  background-color: #003366;
}

.ecdis-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ecdis-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
}

.placeholder-display {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #ffffff;
  text-align: center;
}

.placeholder-content {
  background: rgba(0, 0, 0, 0.7);
  padding: 30px;
  border-radius: 10px;
  border: 2px dashed rgba(255, 255, 255, 0.3);
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  text-align: center;
}

/* Мобильные стили */
@media (max-width: 991px) {
  .control-panel {
    margin-bottom: 15px;
  }

  .ecdis-container {
    height: 400px;
    min-height: 400px;
  }

  .navigation-buttons .btn {
    width: 35px;
    height: 35px;
  }
}
</style>
